import { useEffect, useRef } from 'react';
import { Star, Archive, Trash2 } from 'lucide-react';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import { formatRelativeTime, formatEmailAddress, getCategoryColor, formatCategory, getPriorityColor, formatPriority } from '@/utils/formatters';

export default function EmailList({
  emails,
  selectedId,
  onSelect,
  onStar,
  onArchive,
  onDelete,
  selectedIds = [],
  onSelectEmail,
  onSelectAll
}) {
  const selectedRef = useRef(null);

  // Scroll selected email into view
  useEffect(() => {
    if (selectedId && selectedRef.current) {
      selectedRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [selectedId]);
  if (!emails || emails.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No emails found
      </div>
    );
  }

  const hasSelection = selectedIds.length > 0;
  const allSelected = emails.length > 0 && selectedIds.length === emails.length;

  return (
    <div className="space-y-2">
      {/* Select All Header */}
      {onSelectAll && (
        <div className="flex items-center gap-3 px-4 py-2 border-b">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={(e) => onSelectAll(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="text-sm text-muted-foreground">
            {hasSelection ? `${selectedIds.length} selected` : 'Select all'}
          </span>
        </div>
      )}

      {emails.map((email) => {
        const isSelected = selectedIds.includes(email.id);

        return (
        <div
          key={email.id}
          ref={selectedId === email.id ? selectedRef : null}
          className={`p-2 sm:p-4 rounded-lg border cursor-pointer transition-colors ${
            selectedId === email.id
              ? 'bg-accent border-primary'
              : isSelected
              ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-300 dark:border-blue-700'
              : 'hover:bg-accent'
          } ${!email.seen ? 'border-l-[3px] border-l-blue-500' : ''}`}
          onClick={() => onSelect(email)}
        >
          <div className="flex items-start gap-2 sm:gap-4">
            {/* Selection Checkbox */}
            {onSelectEmail && (
              <div className="flex items-center pt-1">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelectEmail(email.id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
              </div>
            )}

            {/* Avatar/Initial - Hidden on mobile to save space */}
            <div className="hidden sm:flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold flex-shrink-0">
              {formatEmailAddress(email.from_address).charAt(0).toUpperCase()}
            </div>

            {/* Email Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-1 sm:gap-2 mb-1">
                <div className="flex-1 min-w-0">
                  <p className={`${!email.seen ? 'font-semibold' : 'font-medium'} text-sm sm:text-base truncate`}>
                    {formatEmailAddress(email.from_address)}
                  </p>
                  <p className={`text-xs sm:text-sm truncate ${!email.seen ? 'font-bold text-foreground' : 'text-muted-foreground'}`}>
                    {email.subject || '(No subject)'}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0 ml-1">
                  {formatRelativeTime(email.date)}
                </span>
              </div>

              {/* Summary - Hidden on mobile to save space */}
              {email.summary && (
                <p className="hidden sm:block text-sm text-muted-foreground line-clamp-2 mt-1">
                  {email.summary}
                </p>
              )}

              {/* Badges and Actions */}
              <div className="flex items-center justify-between mt-2 sm:mt-3 gap-2">
                <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                  {email.category && (
                    <Badge className={`${getCategoryColor(email.category)} text-xs`}>
                      {formatCategory(email.category)}
                    </Badge>
                  )}
                  {email.priority && (
                    <Badge className={`${getPriorityColor(email.priority)} text-xs hidden sm:inline-flex`}>
                      {formatPriority(email.priority)}
                    </Badge>
                  )}
                  {email.action_needed && (
                    <Badge variant="warning" className="text-xs">Action</Badge>
                  )}
                </div>

                {/* Quick Actions - Only star visible on mobile */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1 h-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      onStar?.(email);
                    }}
                  >
                    <Star
                      className={`h-4 w-4 ${
                        email.starred ? 'fill-yellow-500 text-yellow-500' : ''
                      }`}
                    />
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="hidden sm:inline-flex p-1 h-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      onArchive?.(email);
                    }}
                  >
                    <Archive className="h-4 w-4" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="hidden sm:inline-flex p-1 h-auto text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm('Delete this email?')) {
                        onDelete?.(email);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
}
